import { FlagRule } from './types';

/** Base flag key for an item, stripping any "#shard" suffix. */
export const baseFlagKey = (flagKey: string): string => (flagKey || '').split('#')[0];

const union = (a?: string[], b?: string[]): string[] => {
  const set = new Set<string>();
  (a || []).forEach(x => { if (x) set.add(x); });
  (b || []).forEach(x => { if (x) set.add(x); });
  return Array.from(set);
};

/**
 * Merge sharded flag items into one rule per agent so a flag's audience can
 * grow beyond DynamoDB's 400 KB per-item limit without schema changes.
 *
 * A flag may be split across multiple items that share a base key:
 *   "agentic.exception"           → base item: scalar config
 *                                   (enabled, resourceScope.includeDescendants, raisedWindow)
 *   "agentic.exception#aud#0001"  → audience/resource shard
 *   "agentic.exception#aud#0002"  → audience/resource shard
 *
 * Lists (aliases, posixGroups, policyIds, folderIds) are UNIONed across every
 * item sharing the base key. Scalar config comes from the base item (the one
 * whose flagKey has no "#"). If no base item exists, the merged flag is
 * disabled (fail closed). Flags with no shards are returned unchanged in
 * meaning, just normalised.
 */
export const mergeShardedRules = (rules: FlagRule[]): FlagRule[] => {
  const groups = new Map<string, FlagRule[]>();
  for (const r of rules || []) {
    if (!r || !r.flagKey) continue;
    const base = baseFlagKey(r.flagKey);
    const arr = groups.get(base) || [];
    arr.push(r);
    groups.set(base, arr);
  }

  const merged: FlagRule[] = [];
  groups.forEach((items, baseKey) => {
    const baseItem = items.find(i => i.flagKey === baseKey);

    let aliases: string[] = [];
    let posixGroups: string[] = [];
    let policyIds: string[] = [];
    let folderIds: string[] = [];
    for (const i of items) {
      aliases = union(aliases, i.audience?.aliases);
      posixGroups = union(posixGroups, i.audience?.posixGroups);
      policyIds = union(policyIds, i.resourceScope?.policyIds);
      folderIds = union(folderIds, i.resourceScope?.folderIds);
    }

    // Scalar config: prefer the base item; otherwise any item that sets it.
    const idFromBase = baseItem?.resourceScope?.includeDescendants;
    const includeDescendants = idFromBase !== undefined
      ? idFromBase
      : (items.find(i => i.resourceScope?.includeDescendants !== undefined)?.resourceScope?.includeDescendants ?? true);
    const raisedWindow = baseItem?.raisedWindow ?? items.find(i => i.raisedWindow)?.raisedWindow;

    merged.push({
      flagKey: baseKey,
      // Fail closed: a flag with only shards and no base item is disabled.
      enabled: baseItem ? !!baseItem.enabled : false,
      audience: { aliases, posixGroups },
      resourceScope: { policyIds, folderIds, includeDescendants },
      raisedWindow,
      updatedBy: baseItem?.updatedBy,
      updatedAt: baseItem?.updatedAt,
    });
  });

  return merged;
};
